import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ApiGeneralDataResponse,
  BasicBotData,
  BotData,
  BotType,
  DisplayResultMessage,
  GetBotsDataRequest,
  OpenMessage,
  QueryStatus,
  SortingOrder,
} from "@shared/types";
import {
  BulkDeleteBotsModalProps,
  BulkUpdateBotsModalProps,
  MyBotsSort,
} from "@shared/features/bots/types";
import useMyBots from "features/bots/useMyBots";
import BotsBulkActionsProvider from "@shared/features/bots/BotsBulkActionsProvider";
import { UseQuotaModal } from "@shared/features/settings/types";
import {
  CHANNEL_CONFIGURATIONS_SORTS,
  HEDGING_BOTS_SORTS,
  SIGNALS_BOTS_SORTS,
  TRADING_VIEW_SIGNALS_BOTS_SORTS,
} from "@shared/features/bots/constants";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { SkipToken } from "@reduxjs/toolkit/query";

interface MyBotsPageProviderProps {
  openMessage: OpenMessage;
  active?: boolean;
  botType: BotType;
  searchValue?: string;
  openActionMessage?: OpenMessage;
  dropdownAlertComponent?: ReactNode;
  navigateToBulkEdit?: (botsIds: number[]) => void;
  BulkDeleteBotsModal?: FC<BulkDeleteBotsModalProps>;
  BulkUpdateBotsModal?: FC<BulkUpdateBotsModalProps>;
  useQuotaModal?: UseQuotaModal;
  displayResultMessage?: DisplayResultMessage;
  isChannelConfiguration?: boolean;
  isPublicBots?: boolean;
}

const MyBotsPageProvider = ({
  openMessage,
  active = true,
  botType,
  searchValue,
  openActionMessage,
  dropdownAlertComponent,
  navigateToBulkEdit,
  BulkDeleteBotsModal,
  BulkUpdateBotsModal,
  useQuotaModal,
  displayResultMessage,
  isChannelConfiguration,
  isPublicBots,
  children,
}: PropsWithChildren<MyBotsPageProviderProps>) => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const getDefaultSortBy = () => {
    if (isChannelConfiguration || isTradingViewSignalsBot || isHedgingBot)
      return MyBotsSort.NAME;
    else if (isSignalsBot) return MyBotsSort.LAST_TRADE_OPENED;
    else return MyBotsSort.NEXT_TAKE_PROFIT;
  };
  const [sortBy, setSortBy] = useState<MyBotsSort>(getDefaultSortBy());

  const sortOptions: MyBotsSort[] = useMemo(
    () =>
      Object.values(MyBotsSort).filter((sortBy) => {
        if (isChannelConfiguration) {
          return CHANNEL_CONFIGURATIONS_SORTS.has(sortBy);
        }
        if (isSignalsBot) {
          return SIGNALS_BOTS_SORTS.has(sortBy);
        }
        if (isGridBot) {
          return ![
            MyBotsSort.CLOSED_TRADES,
            MyBotsSort.TOTAL_PROFIT,
            MyBotsSort.OPEN_TRADES,
          ].includes(sortBy);
        }
        if (isTradingViewSignalsBot) {
          return TRADING_VIEW_SIGNALS_BOTS_SORTS.has(sortBy);
        }
        if (isHedgingBot) {
          return HEDGING_BOTS_SORTS.has(sortBy);
        }
        const baseTvAndDcaIgnoreSorts = [
          MyBotsSort.TAKE_PROFITS_FILLED,
          MyBotsSort.REALIZED_PNL,
          MyBotsSort.GROUP,
        ];
        if (!isTradingViewBot) {
          baseTvAndDcaIgnoreSorts.push(MyBotsSort.OPEN_TRADES);
        }
        return !baseTvAndDcaIgnoreSorts.includes(sortBy);
      }),
    [isChannelConfiguration, botType]
  );

  const isValidSort = sortOptions.includes(sortBy);

  const validSortBy = isValidSort ? sortBy : getDefaultSortBy();
  const [sortDirection, setSortDirection] = useState<SortingOrder>(
    SortingOrder.ASCEND
  );
  const {
    bots,
    isFetching,
    getBotsDataStatus,
    unfilteredBots,
    getBotsDataArgs,
  } = useMyBots({
    botType,
    sortBy: validSortBy,
    sortDirection,
    searchValue,
    active,
    isChannelConfiguration,
  });

  useEffect(() => {
    if (!isValidSort) setSortBy(getDefaultSortBy());
  }, [sortBy, botType]);

  const contextValue = useMemo(() => {
    return {
      active,
      botType,
      bots,
      unfilteredBots,
      sortOptions,
      sortBy: validSortBy,
      setSortBy,
      sortDirection,
      setSortDirection,
      isFetching,
      isChannelConfiguration,
      isPublicBots,
      getBotsDataStatus: getBotsDataStatus as QueryStatus<
        ApiGeneralDataResponse<BotData[] | BasicBotData[]>
      >,
      getBotsDataArgs: getBotsDataArgs as GetBotsDataRequest | SkipToken,
    };
  }, [
    active,
    botType,
    bots,
    sortOptions,
    validSortBy,
    sortDirection,
    isFetching,
    isChannelConfiguration,
    isPublicBots,
    getBotsDataStatus,
    getBotsDataArgs,
  ]);

  return (
    <MyBotsPageContext.Provider value={contextValue}>
      <BotsBulkActionsProvider
        navigateToBulkEdit={navigateToBulkEdit}
        openMessage={openMessage}
        openActionMessage={openActionMessage}
        dropdownAlertComponent={dropdownAlertComponent}
        BulkDeleteBotsModal={BulkDeleteBotsModal}
        BulkUpdateBotsModal={BulkUpdateBotsModal}
        useQuotaModal={useQuotaModal}
        displayResultMessage={displayResultMessage}
      >
        {children}
      </BotsBulkActionsProvider>
    </MyBotsPageContext.Provider>
  );
};

export default React.memo(MyBotsPageProvider);
